import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { VolunteeringActionsMenu } from '../../VolunteeringActionsMenu/VolunteeringActionsMenu'
import { CancelOpportunityModal } from '../../CancelOpportunity/Modal/CancelOpportunityModal'
import { CancelOpportunityErrorModal } from '../../CancelOpportunity/ErrorModal/CancelOpportunityErrorModal'
import { CancelOpportunitySuccessModal } from '../../CancelOpportunity/SuccessModal/CancelOpportunitySuccessModal'
import { CloseOpportunityModal } from '../../CloseOpportunity/Modal/CloseOpportunityModal'
import { CloseOpportunitySuccessModal } from '../../CloseOpportunity/SuccessModal/CloseOpportunitySuccessModal'
import { CloseOpportunityErrorModal } from '../../CloseOpportunity/ErrorModal/CloseOpportunityErrorModal'
import { formatAttendeesCount } from '../../formatAttendeesCount'

import {
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  ButtonText,
  useToast
} from '@percent/lemonade'
import { env } from '@percent/partner-dashboard/env'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { APIErrorHandler } from '@percent/partner-dashboard/common/library/APIErrorHandler'
import { downloadCsvFile } from '@percent/partner-dashboard/common/library/utility/utility'
import { ErrorView, OpportunityStatusBadge } from '@percent/partner-dashboard/common/components'
import { VolunteeringSessionsTableProps } from './VolunteeringSessionsTable.types'
import { useMutation } from '@percent/partner-dashboard/common/hooks'
import {
  CancelOpportunityErrorCode,
  CloseOpportunityErrorCode,
  OpportunityStatus,
  OpportunityType
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useCopyAttendeesEmails } from '@percent/partner-dashboard/common/hooks/useCopyAttendeesEmails/useCopyAttendeesEmails'

function SessionNameButton({
  opportunityId,
  opportunityName,
  fullName,
  onClick
}: {
  opportunityId: string
  opportunityName: string
  fullName: string
  onClick: () => void
}) {
  const button = (
    <ButtonText onClick={onClick} variant="primary" data-testid={`session-name-button-${opportunityId}`}>
      {opportunityName}
    </ButtonText>
  )

  if (fullName.length > 38) {
    return <Tooltip content={fullName}>{button}</Tooltip>
  }

  return button
}

export function VolunteeringSessionsTable({
  data,
  isLoading,
  totalResults,
  errorMessage,
  nextPage,
  previousPage,
  refresh
}: Readonly<VolunteeringSessionsTableProps>) {
  const { t } = useTranslation()
  const {
    workplaceGivingService,
    reportingService: { getParticipantDataReport }
  } = useServices()
  const { addToast } = useToast()
  const { userCan } = usePermissionCheck()

  const [selectedCancelOpportunity, setSelectedCancelOpportunity] = useState<{ id: string; name: string } | undefined>()
  const [selectedCloseOpportunity, setSelectedCloseOpportunity] = useState<{ id: string; name: string } | undefined>()
  const [cancelErrorModalOpen, setCancelErrorModalOpen] = useState(false)
  const [cancelSuccessModalOpen, setCancelSuccessModalOpen] = useState(false)
  const [closeErrorModalOpen, setCloseErrorModalOpen] = useState(false)
  const [closeSuccessModalOpen, setCloseSuccessModalOpen] = useState(false)
  const [errorCode, setErrorCode] = useState<CancelOpportunityErrorCode | undefined>(undefined)
  const [closeErrorCode, setCloseErrorCode] = useState<CloseOpportunityErrorCode | undefined>(undefined)

  const [{ isLoading: isCancelLoading, error }, { apiFunc: cancelOpportunity }] = useMutation(
    workplaceGivingService.cancelVolunteeringOpportunity,
    res => {
      if (res.data?.error) {
        setCancelErrorModalOpen(true)
        setErrorCode(res.data?.error?.code)
      } else {
        setCancelSuccessModalOpen(true)
        refresh()
      }
    },
    err => {
      if (err) {
        setCancelErrorModalOpen(true)
      }
    }
  )

  const [{ isLoading: isCloseLoading }, { apiFunc: closeOpportunity }] = useMutation(
    workplaceGivingService.closeVolunteeringOpportunity,
    res => {
      if (res.data?.error) {
        setCloseErrorModalOpen(true)
        setCloseErrorCode(res.data?.error?.code)
      } else {
        setCloseSuccessModalOpen(true)
        refresh()
      }
    },
    err => {
      if (err) {
        setCloseErrorModalOpen(true)
      }
    }
  )

  const [csvDownloaderLoading, setCsvDownloaderLoading] = useState(false)
  const { copyEmails } = useCopyAttendeesEmails()

  const handleExportParticipantData = async (opportunityId: string) => {
    setCsvDownloaderLoading(true)
    try {
      const csvData = await getParticipantDataReport({ opportunityId })
      downloadCsvFile(csvData, `participant_data_${opportunityId}`)
      addToast(t('toast.volunteering.successMessage'), 'success')
    } catch (err: any) {
      addToast(APIErrorHandler(err?.response?.data?.error), 'error')
    } finally {
      setCsvDownloaderLoading(false)
    }
  }

  const handleCloseOpportunity = () => {
    if (selectedCloseOpportunity?.id) {
      setCloseErrorCode(undefined)
      closeOpportunity(selectedCloseOpportunity.id)
      setSelectedCloseOpportunity(undefined)
    }
  }

  const handleCancelOpportunity = () => {
    if (selectedCancelOpportunity?.id) {
      setErrorCode(undefined)
      cancelOpportunity(selectedCancelOpportunity.id)
      setSelectedCancelOpportunity(undefined)
    }
  }

  const columns = useMemo(
    () => [
      { id: t('table.header.name'), props: { width: '50%' } },
      { id: t('table.header.attendees'), props: { width: '15%' } },
      { id: t('table.header.createdAt'), props: { width: '20%' } },
      { id: t('table.header.status'), props: { width: '10%' } },
      { id: '', props: { width: '5%' } }
    ],
    [t]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="no-results"
        emptyTableTitle={t('typography.noVolunteeringOpportunitiesSessionsTitle')}
        emptyTableDescription={t('typography.noVolunteeringOpportunitiesSessionsDescription')}
      >
        <Table>
          <TableBody>
            {data?.map(opportunity => {
              const opportunityHref = `${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${opportunity.id}`
              const opportunityEditAttendanceHref = `${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${opportunity.id}/edit-attendance`
              const opportunityName =
                opportunity.name.length > 38 ? `${opportunity.name.substring(0, 35)}...` : opportunity.name

              const handleViewOpportunity = () => {
                window.open(opportunityHref, '_blank', 'noopener noreferrer')
              }

              return (
                <TableRow key={opportunity.id}>
                  <TableCell>
                    <SessionNameButton
                      opportunityId={opportunity.id}
                      opportunityName={opportunityName}
                      fullName={opportunity.name}
                      onClick={handleViewOpportunity}
                    />
                  </TableCell>
                  <TableCell>{formatAttendeesCount(opportunity, t)}</TableCell>
                  <TableCell>{getFormattedDate(opportunity.createdAt)}</TableCell>
                  <TableCell>
                    <OpportunityStatusBadge status={opportunity.status} />
                  </TableCell>
                  <TableCell>
                    <VolunteeringActionsMenu
                      items={[
                        {
                          key: 'view',
                          label: t('button.view')
                        },
                        ...([OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED].includes(opportunity.status) &&
                        userCan('manageOpportunities')
                          ? [
                              {
                                key: 'editAttendance',
                                label: t('volunteering.editAttendance')
                              }
                            ]
                          : []),
                        ...(opportunity.status === OpportunityStatus.ACTIVE &&
                        (!opportunity.startDate || new Date(opportunity.startDate) < new Date()) &&
                        userCan('manageOpportunities') &&
                        !opportunity.external
                          ? [
                              {
                                key: 'closeOpportunity',
                                label: t('button.close'),
                                dataTestId: 'close-opportunity'
                              }
                            ]
                          : []),
                        ...(opportunity.status === OpportunityStatus.ACTIVE &&
                        (!opportunity.startDate || new Date(opportunity.startDate) > new Date()) &&
                        !opportunity.external
                          ? [
                              {
                                key: 'cancel',
                                label: t('button.cancel')
                              }
                            ]
                          : []),
                        ...(opportunity.type !== OpportunityType.EVENT_SERIES &&
                        opportunity.status !== OpportunityStatus.DRAFT
                          ? [
                              {
                                key: 'copyEmails',
                                label: t('button.copyEmails'),
                                dataTestId: 'copy-emails'
                              }
                            ]
                          : []),
                        // No client-side permission check — the backend controls CSV content
                        // based on user permissions (e.g. survey data only included if user has survey permissions).
                        // Series: GET participant-data returns opportunity_is_event_series (same as workplace-giving).
                        // Draft sessions have no real participants yet, so participant export is hidden.
                        ...(opportunity.participants.count > 0 &&
                        opportunity.type !== OpportunityType.EVENT_SERIES &&
                        opportunity.status !== OpportunityStatus.DRAFT
                          ? [
                              {
                                key: 'exportParticipantData',
                                label: t('button.exportParticipantData'),
                                tooltipInfo: t('button.exportParticipantDataTooltip'),
                                dataTestId: 'export-participant-data'
                              }
                            ]
                          : [])
                      ]}
                      handleSelect={(actionKey: string) => {
                        switch (actionKey) {
                          case 'view':
                            window.open(opportunityHref, '_blank', 'noopener noreferrer')
                            break
                          case 'editAttendance':
                            window.open(opportunityEditAttendanceHref, '_blank', 'noopener noreferrer')
                            break
                          case 'closeOpportunity':
                            setSelectedCloseOpportunity({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'cancel':
                            setSelectedCancelOpportunity({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'copyEmails':
                            copyEmails(opportunity.id)
                            break
                          case 'exportParticipantData':
                            if (!csvDownloaderLoading) {
                              handleExportParticipantData(opportunity.id)
                            }
                            break
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      {selectedCancelOpportunity ? (
        <CancelOpportunityModal
          open={!!selectedCancelOpportunity}
          opportunityName={selectedCancelOpportunity.name}
          handleCancelOpportunity={handleCancelOpportunity}
          handleClose={() => {
            setSelectedCancelOpportunity(undefined)
          }}
          isLoading={isCancelLoading}
        />
      ) : null}
      <CancelOpportunitySuccessModal
        open={cancelSuccessModalOpen}
        handleClose={() => {
          setCancelSuccessModalOpen(false)
        }}
      />
      {error || errorCode ? (
        <CancelOpportunityErrorModal
          open={cancelErrorModalOpen}
          handleClose={() => {
            setCancelErrorModalOpen(false)
          }}
          errorCode={(error?.code as CancelOpportunityErrorCode) || errorCode}
        />
      ) : null}
      {selectedCloseOpportunity ? (
        <CloseOpportunityModal
          open={!!selectedCloseOpportunity}
          opportunityName={selectedCloseOpportunity.name}
          handleCloseOpportunity={handleCloseOpportunity}
          handleClose={() => {
            setSelectedCloseOpportunity(undefined)
          }}
          isLoading={isCloseLoading}
        />
      ) : null}
      <CloseOpportunitySuccessModal
        open={closeSuccessModalOpen}
        handleClose={() => {
          setCloseSuccessModalOpen(false)
        }}
      />
      {error || closeErrorCode ? (
        <CloseOpportunityErrorModal
          open={closeErrorModalOpen}
          handleClose={() => {
            setCloseErrorModalOpen(false)
          }}
          errorCode={(error?.code as CloseOpportunityErrorCode) || closeErrorCode}
        />
      ) : null}
    </>
  )
}
